import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { MessageSquare, Send, ArrowLeft, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Any component can open the chat: openChat({ seller_id }) | openChat({ buyer_id }) | openChat({ conversation_id })
export const openChat = (detail) => window.dispatchEvent(new CustomEvent('restosupply:open-chat', { detail }));

const fmtTime = (iso) => new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

const ChatButton = () => {
  const { user, getAuthHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null); // conversation object
  const [messages, setMessages] = useState([]);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const headers = useCallback(() => ({ headers: { Authorization: getAuthHeader() } }), [getAuthHeader]);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/unread`, headers());
      setUnread(res.data.unread || 0);
    } catch (_) { /* silent */ }
  }, [headers]);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/conversations`, headers());
      setConversations(res.data);
    } catch (_) { /* silent */ }
  }, [headers]);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const res = await axios.get(`${API}/chat/conversations/${convId}/messages`, headers());
      setMessages(res.data.messages);
      setChatEnabled(res.data.chat_enabled);
      setActive(res.data.conversation);
      fetchUnread();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar la conversación');
    }
  }, [headers, fetchUnread]);

  useEffect(() => {
    if (!user) return undefined;
    fetchUnread();
    const t = setInterval(fetchUnread, 15000);
    return () => clearInterval(t);
  }, [user, fetchUnread]);

  useEffect(() => {
    if (!open) return undefined;
    fetchConversations();
    const t = setInterval(() => {
      fetchConversations();
      if (active) fetchMessages(active.id);
    }, 5000);
    return () => clearInterval(t);
  }, [open, active, fetchConversations, fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openWith = useCallback(async (detail) => {
    setOpen(true);
    try {
      if (detail.conversation_id) {
        await fetchMessages(detail.conversation_id);
        return;
      }
      const res = await axios.post(`${API}/chat/conversations`, detail, headers());
      await fetchMessages(res.data.id);
      fetchConversations();
    } catch (err) {
      const code = err.response?.data?.detail;
      if (code === 'seller_not_subscribed') toast.error('Este proveedor no tiene el chat activo (requiere suscripción).');
      else if (code === 'subscription_required_chat') toast.error('Necesitas una suscripción activa para usar el chat.');
      else toast.error(code || 'No se pudo abrir el chat');
    }
  }, [fetchMessages, fetchConversations, headers]);

  useEffect(() => {
    const handler = (e) => openWith(e.detail || {});
    window.addEventListener('restosupply:open-chat', handler);
    return () => window.removeEventListener('restosupply:open-chat', handler);
  }, [openWith]);

  const send = async () => {
    const t = text.trim();
    if (!t || !active) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat/conversations/${active.id}/messages`, { text: t }, headers());
      setMessages((prev) => [...prev, res.data]);
      setText('');
      fetchConversations();
    } catch (err) {
      const code = err.response?.data?.detail;
      if (code === 'seller_not_subscribed') toast.error('Este proveedor no tiene el chat activo.');
      else if (code === 'subscription_required_chat') toast.error('Necesitas una suscripción activa para chatear.');
      else toast.error(code || 'No se pudo enviar');
    } finally {
      setSending(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <Button variant="ghost" size="sm" data-testid="chat-button" onClick={() => { setActive(null); setOpen(true); }} className="relative text-slate-600 hover:text-slate-900">
        <MessageSquare size={20} />
        {unread > 0 && (
          <span data-testid="chat-unread-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setActive(null); }}>
        <DialogContent data-testid="chat-dialog" className="max-w-lg p-0 overflow-hidden h-[80vh] flex flex-col">
          <DialogHeader className="p-4 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-2">
              {active && (
                <button type="button" onClick={() => setActive(null)} data-testid="chat-back-button" className="text-slate-500 hover:text-slate-900">
                  <ArrowLeft size={18} />
                </button>
              )}
              {active ? active.other_party_name : 'Mensajes'}
            </DialogTitle>
            <DialogDescription>
              {active ? 'Conversación directa en la app' : 'Chatea con tus contactos de RestoSupply'}
            </DialogDescription>
          </DialogHeader>

          {!active ? (
            <div className="flex-1 overflow-y-auto" data-testid="chat-conversations">
              {conversations.length === 0 ? (
                <p className="p-6 text-sm text-slate-500 text-center" data-testid="chat-empty">Aún no tienes conversaciones. Abre un chat desde un pedido, una puja o el perfil de un proveedor.</p>
              ) : conversations.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`chat-conversation-${c.id}`}
                  onClick={() => fetchMessages(c.id)}
                  className="w-full text-left p-3 border-b border-slate-50 hover:bg-slate-50 flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm overflow-hidden flex-shrink-0">
                    {c.other_party_logo ? <img src={c.other_party_logo} alt="" className="w-full h-full object-cover" /> : (c.other_party_name || '?').charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{c.other_party_name}</p>
                    <p className="text-xs text-slate-500 truncate">{c.last_message || 'Sin mensajes todavía'}</p>
                  </div>
                  {c.unread > 0 && (
                    <span className="min-w-[20px] h-5 px-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center">{c.unread}</span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50" data-testid="chat-messages">
                {messages.length === 0 && <p className="text-xs text-slate-400 text-center py-6">Escribe el primer mensaje</p>}
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`chat-message-${m.id}`}>
                      <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm shadow-sm ${mine ? 'bg-emerald-600 text-white rounded-br-sm' : 'bg-white text-slate-900 rounded-bl-sm border border-slate-200'}`}>
                        <p className="whitespace-pre-wrap break-words">{m.text}</p>
                        <p className={`text-[10px] mt-1 ${mine ? 'text-emerald-100' : 'text-slate-400'}`}>{fmtTime(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              {chatEnabled ? (
                <div className="p-3 border-t border-slate-100 flex gap-2">
                  <input
                    data-testid="chat-input"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Escribe un mensaje…"
                    maxLength={2000}
                    className="flex-1 rounded-full border border-slate-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <Button onClick={send} disabled={sending || !text.trim()} data-testid="chat-send-button" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4">
                    <Send size={16} />
                  </Button>
                </div>
              ) : (
                <div className="p-3 border-t border-amber-200 bg-amber-50 text-xs text-amber-900 flex items-center gap-2" data-testid="chat-locked">
                  <Lock size={14} /> El chat está desactivado: el proveedor necesita una suscripción activa.
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatButton;
